import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  Input,
  signal,
  inject,
} from '@angular/core';
import { MediaMatcher } from '@angular/cdk/layout';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { rolesIncludeSuperadmin } from '@core/auth/superadmin-access';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import { ADMIN_SHELL_NAV_ICONS } from '@core/shell/admin-shell-nav-icons';
import { AppHorizontalNavItemComponent } from './nav-item/nav-item.component';
import { NavItem } from '../../vertical/sidebar/nav-item/nav-item';

@Component({
  selector: 'app-horizontal-sidebar',
  imports: [AppHorizontalNavItemComponent, TranslateModule],
  templateUrl: './sidebar.component.html',
})
export class AppHorizontalSidebarComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly authSession = inject(AuthSessionService);
  private readonly adminShellMode = inject(AdminShellModeService);

  /** Menú dinámico del shell (roles / plan / platform); lo inyecta `FullComponent`. */
  @Input() navItems: NavItem[] = [];

  /** Un solo dropdown abierto a la vez (id de módulo o null). */
  readonly openModuleId = signal<number | string | null>(null);

  mobileQuery: MediaQueryList;
  private readonly mobileQueryListener: () => void;
  private navSub = Subscription.EMPTY;

  private readonly onDocPointerDown = (event: PointerEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    if (target.closest('app-horizontal-nav-item.parentBox, .horizontal-navbar__context')) {
      return;
    }
    this.openModuleId.set(null);
  };

  constructor(media: MediaMatcher, changeDetectorRef: ChangeDetectorRef) {
    this.mobileQuery = media.matchMedia('(min-width: 1100px)');
    this.mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this.mobileQueryListener);
  }

  ngOnInit(): void {
    document.addEventListener('pointerdown', this.onDocPointerDown, true);
    this.navSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.openModuleId.set(null));
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this.mobileQueryListener);
    document.removeEventListener('pointerdown', this.onDocPointerDown, true);
    this.navSub.unsubscribe();
  }

  trackNavItem(item: NavItem, index: number): string {
    return String(item.id ?? item.route ?? item.displayName ?? index);
  }

  onOpenModule(id: number | string | null): void {
    this.openModuleId.set(id);
  }

  /** Misma entrada que el rail vertical: switch tenant ↔ plataforma (superadmin). */
  showContextSwitch(): boolean {
    return (
      rolesIncludeSuperadmin(this.authSession.user()?.roles) &&
      this.adminShellMode.mode() !== 'pos'
    );
  }

  contextSwitchIcon(): string {
    // Evitar el mismo icono que Inicio (`home-2`) al volver a Milinco.
    return this.adminShellMode.mode() === 'platform'
      ? 'solar:shop-2-line-duotone'
      : ADMIN_SHELL_NAV_ICONS.switchToPlatform;
  }

  contextSwitchLabelKey(): string {
    return this.adminShellMode.mode() === 'platform'
      ? 'shell.goToMilinco'
      : 'shell.goToPlatform';
  }

  onContextSwitch(): void {
    this.openModuleId.set(null);
    this.adminShellMode.toggleContextSwitch();
  }
}
